import React, { useEffect, useRef, useState } from "react";
import { CaseClient, IDVInformation, VerifiedClient } from "../../../types";
import { Alert, Button, Card, Col, Dropdown, Row } from "react-bootstrap";
import { runInnerFunction } from "../../../../../compatManager";
import IDVImportMatching from "./IDVImportMatching";
import { format } from 'date-fns';
import { evaluateIDVResult, extractIDVInformation, isPreReconfigSubmission } from "./IDVUtils";
import IDVLegacy from "./IDVLegacy";

const DISCLAIMER_TEXT =
    "Disclaimer: The ClientConnect ID Verification can produce a high quality analysis of an ID document and is provided with the understanding that LDD is not engaged in rendering legal or other professional advice. You should always rely on your own professional judgment and experience in assessing the authenticity of an ID document.";

const IMPORT_CONFIRMATION_TEXT = "ID information has been imported into the Realti file.";

const ID_INFO_ROWS: { key: keyof IDVInformation; label: string }[] = [
    { key: 'givenName', label: 'Given Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'sex', label: 'Sex' },
    { key: 'idType', label: 'ID Type' },
    { key: 'issuingJurisdiction', label: 'Issuing Jurisdiction' },
    { key: 'documentNumber', label: 'Document Number' },
    { key: 'dateOfExpiry', label: 'Date of Expiry' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
];

type TProps = {
    idv: VerifiedClient,
    province: string,
    setDirty: () => void,
    matchedClient?: CaseClient,
    // Drives the actual import — set only when this record is the one that will write.
    autoImport?: boolean,
    // The ID is already in the file, written by something other than this record (an earlier record
    // for the same client, or a prior session). Shows the confirmation without a write of our own.
    // A record that was BLOCKED from importing gets neither flag. See IdentityVerificationTaskModal.
    importedElsewhere?: boolean,
    onDownload: () => void,
    onPrint: () => void,
    onRequestReVerification: () => void,
}

// Verifications submitted before the #19619 reconfiguration went live keep the previous on-screen
// format (score + certainty + auto-fail alerts) so already-read results don't change retroactively.
const IDV = (props: TProps) => {
    if (isPreReconfigSubmission(props.idv)) {
        return (
            <IDVLegacy
                idv={props.idv}
                province={props.province}
                setDirty={props.setDirty}
                matchedClient={props.matchedClient}
            />
        );
    }
    return <IDVReconfigured {...props} />;
};

const IDVReconfigured = (props: TProps) => {
    const { idv, setDirty, matchedClient, autoImport, importedElsewhere, onDownload, onPrint, onRequestReVerification } = props;
    const [showImport, setShowImport] = useState(false);
    const [availableIndividuals, setAvailableIndividuals] = useState<any>([]);
    const [selectIndex, setSelectIndex] = useState(-1);
    const [isImported, setIsImported] = useState(false);
    const [autoImportThrew, setAutoImportThrew] = useState(false);

    const evaluation = evaluateIDVResult(idv);
    const information = extractIDVInformation(idv);
    const isFail = evaluation.outcome === 'Fail';
    // Show the confirmation only when the ID actually reached the file: this record wrote it (and the
    // write didn't throw), something else already wrote it, or the user imported it by hand. A mere
    // name match is NOT evidence of a write — a record blocked from importing (unsupported document
    // type, or a failed verification) previously claimed the ID was on file when it wasn't.
    const importComplete = isImported || importedElsewhere || (!!autoImport && !autoImportThrew);

    const handleImport = () => {
        const ret = runInnerFunction('ReactShell.importIDV', { Entity: idv.Entity });

        if (ret) {
            // need to pick a person
            setAvailableIndividuals(ret);
            setShowImport(true);
            setDirty();
        } else {
            // person auto-match, and imported
            setIsImported(true);
        }
    };

    // When the modal resolves this IDV to a specific case client, import automatically instead of
    // making the user open the chooser. Equivalent to picking that client and clicking Accept:
    // importIDVRetry sets Entity.Name to matchedClient.nameFull (the exact FullnameAttr the legacy
    // importIdentification matches against) and runs the import. Guarded so it fires once per mount.
    // Only fires when autoImport is set: a matched record whose client was already imported by an
    // earlier record still shows the confirmation (importComplete) but must not write a duplicate.
    const hasAutoImported = useRef(false);
    useEffect(() => {
        if (!autoImport || !matchedClient?.nameFull || hasAutoImported.current) return;
        hasAutoImported.current = true;
        // The legacy importIdentification can synchronously throw deep inside ajaxslt when the
        // IDV's data doesn't fit the writer's assumptions. The modal already filters out the
        // known case (unsupported document type via isLegacyImportableIDV); this is the safety
        // net for any other shape we haven't catalogued. Failing closed leaves the user with the
        // manual Link-to-Client flow.
        try {
            runInnerFunction('ReactShell.importIDVRetry', {
                Entity: idv.Entity,
                name: matchedClient.nameFull
            });
            setDirty();
        } catch (e) {
            console.warn('IDV auto-import failed; falling back to manual flow', e);
            setAutoImportThrew(true);
        }
    }, [autoImport, matchedClient, idv.Entity, setDirty]);

    const renderIdInformation = () => {
        const populated = ID_INFO_ROWS.filter(row => information[row.key]);
        if (populated.length === 0) {
            return <div className="text-muted">No ID information was extracted.</div>;
        }
        return (
            <div className="idv-info-list">
                {populated.map(row => (
                    <Row key={row.key} className="gx-2 mb-1">
                        <Col xs="6"><span className="text-muted">{row.label}:</span></Col>
                        <Col xs="6"><b>{information[row.key]}</b></Col>
                    </Row>
                ))}
            </div>
        );
    };

    return (
        <React.Fragment>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="mb-0">{idv.clientInformation.Name}</h3>

                <div
                    className="d-flex align-items-center justify-content-center rounded-pill px-2 py-1"
                    style={{
                        backgroundColor: "#cde4ee",
                        color: "#037aa8",
                        fontSize: "10px",
                    }}
                >
                    Received{" "}
                    {format(new Date((idv.clientInformation.Timestamp+"Z").toLocaleString()), "MMMM do, yyyy h:mm a")}
                </div>
            </div>

            <Row className="mb-5">
                <Col xs="4">
                    <Card className="mb-3">
                        <Card.Body>
                            <div className="d-flex align-items-start mb-2">
                                <div
                                    className="d-flex align-items-center justify-content-center me-3"
                                    style={{
                                        background: 'var(--bs-primary)',
                                        color: '#fff',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 8,
                                        flexShrink: 0,
                                    }}
                                >
                                    <i className="fa-regular fa-id-card" style={{ fontSize: '1.25rem', color: '#fff' }}></i>
                                </div>
                                <div className="flex-grow-1">
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td className="pe-2">ID Verification Result:</td>
                                                <td>
                                                    <b style={{ color: isFail ? 'var(--danger, #d9534f)' : 'var(--success, #28a745)' }}>
                                                        {evaluation.outcome}
                                                    </b>
                                                </td>
                                            </tr>
                                            {/* Individual face verification result — shown only when face
                                                verification was performed for this file. The main Pass/Fail
                                                already accounts for face mismatch; this just surfaces the
                                                face check on its own*/}
                                            {!idv.skipFaceVerification && (
                                                <tr>
                                                    <td className="pe-2">Face Verification Result:</td>
                                                    <td>
                                                        <b style={{ color: idv.FaceVerificationPassed ? 'var(--success, #28a745)' : 'var(--danger, #d9534f)' }}>
                                                            {idv.FaceVerificationPassed ? 'Pass' : 'Fail'}
                                                        </b>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    <div className="text-muted fst-italic mt-2" style={{ fontSize: '0.85rem' }}>
                                        {DISCLAIMER_TEXT}
                                    </div>
                                </div>
                            </div>

                            {isFail && evaluation.failMessage && (
                                <Alert variant="danger" className="mt-3 d-flex align-items-start mb-0">
                                    <span>{evaluation.failMessage}</span>
                                </Alert>
                            )}

                            {evaluation.faceVerificationFailed && evaluation.faceMismatchMessage && (
                                <Alert variant="danger" className="mt-2 d-flex align-items-start mb-0">
                                    <span>{evaluation.faceMismatchMessage}</span>
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    <div className="d-flex align-items-center gap-2">
                        {isFail ? (
                            <>
                                <Button onClick={onRequestReVerification} className="button button-primary">Request Re-Verification</Button>
                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="link"
                                        id={`idv-fail-actions-${idv.clientInformation.id || idv.clientInformation.Name}`}
                                        className="no-caret text-decoration-none"
                                    >
                                        <i className="fa-solid fa-ellipsis"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={onDownload}>
                                            <i className="fa-regular fa-download me-2"></i>Download Result
                                        </Dropdown.Item>
                                        <Dropdown.Item onClick={onPrint}>
                                            <i className="fa-regular fa-print me-2"></i>Print Result
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </>
                        ) : (
                            <>
                                <Button className="button-primary" onClick={onDownload}>
                                    <i className="fa-regular fa-download me-1"></i>Download Result
                                </Button>
                                <Button className="outline-primary" onClick={onPrint}>
                                    <i className="fa-regular fa-print me-1"></i>Print Result
                                </Button>
                            </>
                        )}
                    </div>
                </Col>

                <Col xs="5">
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 3, minWidth: 0 }}>
                            <div className="id-container mb-2">
                                <img src={idv.clientIDImages?.frontImage} alt='not-found' />
                            </div>
                            {idv.clientIDImages?.backImage && (
                                <div className="id-container"><img src={idv.clientIDImages?.backImage} alt='not-found' /></div>
                            )}
                        </div>
                        {!idv.skipFaceVerification && idv.clientIDImages?.verifyFaceFrame && (
                            <div style={{ flex: 2, minWidth: 0 }}>
                                <div className="id-container"><img src={idv.clientIDImages?.verifyFaceFrame} alt='not-found' /></div>
                            </div>
                        )}
                    </div>
                </Col>

                <Col xs="3">
                    <Card>
                        <Card.Body>
                            {renderIdInformation()}
                            <div className="mt-3">
                                {importComplete ? (
                                    <div className="d-flex align-items-center" style={{ color: 'var(--success)' }}>
                                        <i className="fa-solid fa-circle-check me-2"></i>
                                        <span>{IMPORT_CONFIRMATION_TEXT}</span>
                                    </div>
                                ) : (
                                    <Button onClick={handleImport}>
                                        <i className="fa-solid fa-plus me-1"></i>Import ID Information
                                    </Button>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {showImport &&
                <IDVImportMatching
                    nameOnID={idv.clientInformation.Name}
                    availableList={availableIndividuals}
                    setPersonIndex={(index: number) => { setSelectIndex(index) }}
                    handleConfirm={() => {
                        setShowImport(false);
                        setTimeout(() => {
                            setIsImported(true);
                            runInnerFunction('ReactShell.importIDVRetry',
                                {
                                    Entity: idv.Entity,
                                    name: availableIndividuals[selectIndex].name
                                }
                            );
                            setDirty();
                        }, 500);
                    }}
                    handleClose={() => { setShowImport(false) }} />
            }
        </React.Fragment>
    );
};

export default IDV;
